import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { HelpCircle, Loader2, Sparkles, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import './CompanyPolicies.css';

const CompanyPolicies = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqAnswers, setFaqAnswers] = useState({});
  const [loadingFaqId, setLoadingFaqId] = useState(null);
  const [hrProfile, setHrProfile] = useState(null);

  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveHR();
    fetchFaqs();
  }, []);

  const fetchActiveHR = async () => {
    try {
      const res = await api.get('/hr/active/');
      setHrProfile(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const faqsRes = await api.get('/company-policy-faqs/');
      setFaqs(faqsRes.data.results || faqsRes.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load policy FAQs.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFaqClick = async (faq) => {
    if (faqAnswers[faq.id]) {
      setFaqAnswers(prev => ({
        ...prev,
        [faq.id]: { ...prev[faq.id], expanded: !prev[faq.id].expanded }
      }));
      return;
    }

    try {
      setLoadingFaqId(faq.id);
      const res = await api.post('/ai/policy/', { question: faq.question });
      setFaqAnswers(prev => ({
        ...prev,
        [faq.id]: {
          answer: res.data.answer,
          sources: res.data.sources || [],
          expanded: true
        }
      }));
    } catch (err) {
      console.error(err);
      addToast('Failed to retrieve AI policy answer.', 'error');
    } finally {
      setLoadingFaqId(null);
    }
  };

  const hrName = hrProfile?.name || 'Sarah Jenkins';
  const hrDesig = hrProfile?.designation || 'Chief HR Officer';
  const hrCompany = hrProfile?.company_name || 'Technaureus Info Solutions';
  const hrAvatar = hrProfile?.avatar_url || hrProfile?.avatar;

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content" style={{ maxWidth: '960px' }}>
          
          {/* Single Unified HR Officer Hero Header */}
          <div className="policies-single-hr-hero">
            <div className="policies-hr-hero-left">
              <div className="policies-hr-avatar-wrapper">
                {hrAvatar ? (
                  <img src={hrAvatar} alt={hrName} className="policies-hr-avatar-img" />
                ) : (
                  <div className="policies-hr-avatar-fallback">{hrName.charAt(0)}</div>
                )}
                <span className="policies-hr-online-dot" />
              </div>

              <div className="policies-hr-hero-info">
                <div className="policies-hr-tag">
                  <Sparkles size={12} /> HR Policy Assistance
                </div>
                <h2>{hrName}</h2>
                <span className="policies-hr-sub">{hrDesig} • {hrCompany}</span>
                <p className="policies-hr-quote">
                  "Hi! I'm here to assist you with company policies, leave rules, timings, and workplace guidelines. Have doubts? Ask me anytime!"
                </p>
              </div>
            </div>

            <div className="policies-hr-hero-right">
              <button className="btn btn-primary btn-lg policies-chat-cta-btn" onClick={() => navigate('/company-policies/chat')}>
                <MessageSquare size={18} />
                <span>Chat with me</span>
              </button>
            </div>
          </div>

          {/* Single Full-Width FAQ Section */}
          <div className="policies-faq-container">
            <div className="policies-faq-header-row">
              <h2 className="policies-section-title">
                <HelpCircle size={22} style={{ color: '#4f46e5' }} /> Frequently Asked Policy Questions
              </h2>
              <span className="badge badge-primary">
                AI Knowledge Base
              </span>
            </div>

            {loading ? (
              <LoadingSpinner label="Loading policy FAQs..." />
            ) : faqs.length === 0 ? (
              <div className="card" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                No policy questions configured yet.
              </div>
            ) : (
              <div className="policies-faq-list-single">
                {faqs.map((faq, index) => {
                  const faqState = faqAnswers[faq.id];
                  const isExpanded = faqState && faqState.expanded;
                  const isLoadingThis = loadingFaqId === faq.id;

                  return (
                    <div
                      key={faq.id}
                      className={`card policies-faq-card-single ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleFaqClick(faq)}
                    >
                      <div className="policies-faq-item-row">
                        <span className="policies-faq-number">
                          {String(faq.order_number || index + 1).padStart(2, '0')}
                        </span>
                        
                        <div className="policies-faq-content">
                          <div className="policies-faq-question-header">
                            <span className="policies-faq-question-text">{faq.question}</span>
                            <div className="policies-faq-icon-right">
                              {isLoadingThis ? (
                                <Loader2 size={18} className="spinner-icon" />
                              ) : isExpanded ? (
                                <ChevronUp size={20} style={{ color: '#4f46e5' }} />
                              ) : (
                                <ChevronDown size={20} style={{ color: '#94a3b8' }} />
                              )}
                            </div>
                          </div>

                          {/* Expanded RAG Answer Box */}
                          {isExpanded && faqState && (
                            <div className="policies-faq-answer-box animate-fade-in">
                              <div className="policies-faq-ai-tag">
                                <Sparkles size={14} /> Answered by {hrName} ({hrDesig})
                              </div>
                              <p className="policies-faq-answer-text">
                                {faqState.answer}
                              </p>
                              {faqState.sources && faqState.sources.length > 0 && (
                                <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {faqState.sources.map((src, idx) => (
                                    <span key={idx} className="badge badge-info" style={{ fontSize: '0.72rem' }}>
                                      Source: {src}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </main>
      </div>
    </div>
  );
};

export default CompanyPolicies;
